import localforage from "localforage";

import * as CONFIG from "../../../config.js";
import { userTokenStorageKey } from "../../../config.js";
import { isEmpty } from "lodash";
import * as TYPES from "./mutations-types.js";
import * as services from "../services.js";
import { decodeJWT } from "../services.js";
import moment from "moment";

export const doLogin = ({ dispatch, commit }, payload) =>
    services.postLogin(payload).then((response) => {
        // Extract both access token (as 'token') and refresh token
        // Backend returns: { token, access, refresh, user }
        // 'token' is a backward-compatible copy of 'access'
        const token = response.token || response.access;
        const refreshToken = response.refresh || null;
        const user = response.user;

        dispatch("successfullLogin", {
            user: user,
            token: token,
            refreshToken: refreshToken,
        });

        return user; // keep promise chain
    });

export const successfullLogin = ({ dispatch, commit }, payload) => {
    dispatch("setUser", payload.user);
    dispatch("setToken", payload.token);
    // Set refresh token if provided (backward compatible)
    if (payload.refreshToken) {
        dispatch("setRefreshToken", payload.refreshToken);
    }

    services
        .getPermissions(payload.token)
        .then((permissions) => {
            commit(TYPES.SET_PERMISSION, permissions);
        })
        .catch((err) => {
            // there are no permissions
            commit(TYPES.SET_PERMISSION, null);
        });
};

export const forgotPassword = ({ dispatch, commit }, payload) => {
    services.forgotPassword(payload.username);
};

export const verifyPasscode = ({ dispatch, commit }, payload) => {
    return services.verifyPasscode(payload.username, payload.key);
};

export const changePassword = ({ dispatch, commit }, payload) => {
    services
        .changePassword(payload.password, payload.token)
        .then(function (result) {
            if (result.status) {
                // Preserve tokens from payload
                const loginPayload = {
                    user: payload.user,
                    token: payload.token || payload.access,
                    refreshToken:
                        payload.refresh || payload.refreshToken || null,
                };
                dispatch("successfullLogin", loginPayload);
            }
        });
};

export const logout = ({ commit, state }) => {
    services.revokeToken(state.token);
    // call actions
    commit(TYPES.TOKEN_EXPIRED);
};

export const setUser = ({ commit }, user) => {
    // Commit the mutations
    commit(TYPES.SET_USER, user);

    Promise.resolve(user); // keep promise chain
};

export const setToken = ({ commit }, payload) => {
    // prevent if payload is a object
    const token = isEmpty(payload) ? null : payload.token || payload;

    // Commit the mutations
    commit(TYPES.SET_TOKEN, token);

    return Promise.resolve(token); // keep promise chain
};

export const setRefreshToken = ({ commit }, payload) => {
    // prevent if payload is a object
    const refreshToken = isEmpty(payload)
        ? null
        : payload.refreshToken || payload;

    // Commit the mutations
    commit(TYPES.SET_REFRESH_TOKEN, refreshToken);

    return Promise.resolve(refreshToken); // keep promise chain
};

export const checkUserToken = ({ dispatch, state }) => {
    // If the token exists then all validation has already been done
    if (!isEmpty(state.token)) {
        return Promise.resolve(state.token);
    }

    // When JWT_COOKIE_ONLY is enabled, don't try to load token from storage
    // The server will handle authentication via cookies
    if (CONFIG.JWT_COOKIE_ONLY) {
        return Promise.reject("NO_TOKEN"); // Reject promise - rely on cookies
    }

    /**
     * Token does not exist yet
     * - Recover it from localstorage
     * - Recover also the user, validating the token also
     */
    return (
        localforage
            .getItem(userTokenStorageKey)
            .then((token) => {
                if (isEmpty(token)) {
                    // Token is not saved in localstorage
                    return Promise.reject("NO_TOKEN"); // Reject promise
                }
                // Put the token in the vuex store
                return dispatch("setToken", token); // keep promise chain
            })
            // With the token in hand, retrieves the user's data, validating the token
            .then(() => dispatch("loadUser"))
    );
};

/**
 * Retrieves updated user information
 * If something goes wrong, the user's token is probably invalid
 */
export const loadUser = ({ dispatch }) =>
    services
        .loadUserData()
        // store user's data
        .then((user) => dispatch("setUser", user.data))
        .catch(() => {
            // Process failure, delete the token
            dispatch("setToken", "");
            return Promise.reject("FAIL_IN_LOAD_USER"); // keep promise chain
        });

export const refreshToken = ({ state, dispatch, commit }) => {
    // Use refresh token if available, otherwise fall back to access token for backward compatibility
    const tokenToUse = state.refreshToken || state.token;

    if (!isEmpty(tokenToUse) && !state.refreshing_token) {
        var invalid = false;
        var about_to_expire = false;

        try {
            // Check the access token expiration, not the refresh token
            var decodedToken = decodeJWT(state.token);

            var expires = moment(decodedToken.exp * 1000);

            // does it expire in less than 30 minutes?
            if (expires.isBefore(moment().add(30, "minutes"))) {
                about_to_expire = true;
            }
        } catch (err) {
            invalid = true;
        }
        if (!invalid && about_to_expire) {
            commit(TYPES.REFRESH_TOKEN); // Only refresh once

            services
                .refreshToken(tokenToUse, !!state.refreshToken)
                .then((response) => {
                    // Extract tokens from response
                    const token = response.token || response.access;
                    const refreshToken = response.refresh || null;
                    const user = response.user;

                    if (user) {
                        dispatch("setUser", user);
                    }
                    dispatch("setToken", token);
                    // Update refresh token if a new one is provided
                    if (refreshToken) {
                        dispatch("setRefreshToken", refreshToken);
                    }

                    commit(TYPES.DONE_REFRESH_TOKEN);
                    return user; // keep promise chain
                })
                .catch(function (err) {
                    commit(TYPES.DONE_REFRESH_TOKEN);
                    return err;
                });
        }
    }
};

export const undo_sudo = ({ state, dispatch, commit }, payload) => {
    commit("POP_RECORD_PREVIOUS");

    dispatch("successfullLogin", { user: state.user, token: state.token });
};

export const sudo = ({ state, dispatch, commit }, payload) => {
    if (!isEmpty(state.token) && !state.refreshing_token) {
        commit("RECORD_PREVIOUS");

        services.sudo(payload, state.token).then((response) => {
            const token = response.token || response.access;
            const refreshToken = response.refresh || null;
            const user = response.user;

            dispatch("successfullLogin", {
                user: user,
                token: token,
                refreshToken: refreshToken,
            });

            return user; // keep promise chain
        });
    }
};
